import type { ComponentProps } from "react";
import Image from "next/image";
import Link from "next/link";
import { renderRichText } from "@storyblok/react";
import { storyblokEditable } from "@storyblok/react/rsc";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { Avatar } from "@/components/layout/Avatar";
import { Headline } from "@/components/typography/Headline";
import { Subline } from "@/components/typography/Subline";
import { RichText } from "@/components/typography/RichText";
import type { TextImageStoryblok } from "@/component-types-sb";
import { cn } from "@/lib/utils";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";

type TextImageProps = { blok: TextImageStoryblok } & ComponentProps<"article">;

const TextImage = ({ blok, ...rest }: TextImageProps) => {
  const team = blok.team as any;
  const isZoomable = blok.zoomable === true;

  // Lightbox data attributes for global event delegation
  const lightboxProps = isZoomable
    ? {
        "data-lightbox-src": blok.image?.filename,
        "data-lightbox-alt": blok.image?.alt || "",
        role: "button",
        tabIndex: 0,
      }
    : {};

  return (
    <>
      {blok.text && blok.headline ? (
        <article
          className={cn(
            "bg-white text-black-95 rounded-3xl flex flex-col-reverse md:flex-row gap-0 md:gap-14 mb-8",
            {
              ["md:flex-row-reverse"]: blok.reverse,
            },
            rest.className
          )}
          {...storyblokEditable(blok)}
        >
          <div className="md:w-2/3 flex p-6 lg:p-8 flex-col items-start md:justify-center">
            <Subline>{blok.subline}</Subline>
            <Headline as="h3" size="medium" className="mb-6 md:my-4">
              {blok.headline}
            </Headline>
            <RichText
              html={renderRichText(blok.text)}
              className="text-copy mb-4"
            />

            {team && (
              <Avatar
                image={team.content?.image?.filename}
                name={team.content?.subheadline}
                text={team.content?.headline}
              />
            )}

            {blok.link && blok.button && (
              <Link href={getUrlFromLinkObject(blok?.link) || "#"}>
                <Button variant="black" size="sm" className="w-full md:w-auto">
                  <ArrowRight size="sm" />
                  {blok.button}
                </Button>
              </Link>
            )}
          </div>
          <div
            className={cn("md:w-1/3 p-4", { "cursor-zoom-in": isZoomable })}
            {...lightboxProps}
          >
            <div className="h-40 md:h-full md:max-h-full">
              <Image
                src={blok.image?.filename}
                alt={blok.image?.alt || ""}
                width={420}
                height={420}
                className={cn(
                  "w-full h-full object-cover rounded-2xl",
                  {
                    ["object-contain"]: blok.contain,
                  }
                )}
              />
            </div>
          </div>
        </article>
      ) : (
        <article
          className={cn("bg-white text-black-95 rounded-3xl p-6 mb-8", rest.className)}
        >
          <div
            className={cn({ "cursor-zoom-in": isZoomable })}
            {...lightboxProps}
          >
            <Image
              src={blok.image?.filename}
              alt={blok.image?.alt || ""}
              width={420}
              height={420}
              className="w-full max-h-[420px] object-cover object-top rounded-2xl"
            />
          </div>
        </article>
      )}
    </>
  );
};

export default TextImage;
